import {
  createTable,
  type RowData,
  type Table,
  type TableOptions,
  type TableOptionsResolved,
  type TableState,
} from "@tanstack/table-core"

export type SvelteTable<TData extends RowData> = Table<TData> & {
  readonly state: Partial<TableState>
}

export function createSvelteTable<TData extends RowData>(
  options: TableOptions<TData>
): SvelteTable<TData> {
  const resolvedOptions = mergeObjects(
    {
      state: {},
      onStateChange() {},
      renderFallbackValue: null,
      mergeOptions: (
        defaultOptions: TableOptions<TData>,
        opts: Partial<TableOptions<TData>>
      ) => mergeObjects(defaultOptions, opts),
    },
    options
  ) as TableOptionsResolved<TData>

  const table = createTable(resolvedOptions)
  let state = $state<Partial<TableState>>(table.initialState)

  function updateOptions() {
    table.setOptions((prev) => {
      // Pass the $state rune itself (not a snapshot) so reads go through
      // its proxy and register Svelte reactive deps. mergeObjects would
      // otherwise copy descriptor values at merge time and freeze them.
      return mergeObjects(prev, options, {
        state: options.state ? mergeObjects(state, options.state) : state,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onStateChange: (updater: any) => {
          if (updater instanceof Function) state = updater(state)
          else state = mergeObjects(state, updater)
          options.onStateChange?.(updater)
        },
      }) as TableOptionsResolved<TData>
    })
  }

  updateOptions()
  $effect.pre(() => {
    updateOptions()
  })

  return new Proxy(table, {
    get(target, prop, receiver) {
      if (prop === "state") return state
      return Reflect.get(target, prop, receiver)
    },
  }) as SvelteTable<TData>
}

// Object.assign invokes getters during merge and freezes values, which
// breaks reactivity for $state proxies — we copy descriptors instead.
function mergeObjects<TOut = unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...sources: Array<Record<string, any> | undefined>
): TOut {
  const out: Record<string, unknown> = {}
  for (const src of sources) {
    if (!src) continue
    for (const key of Object.keys(src)) {
      const desc = Object.getOwnPropertyDescriptor(src, key)
      if (!desc) continue
      Object.defineProperty(out, key, desc)
    }
  }
  return out as TOut
}
